// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';

const apiPrefix = process.env.REACT_APP_API_PREFIX;

// Create context
const AuthContext = createContext();

// Context provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize authentication state
  useEffect(() => {
    const initAuth = async () => {
      // restore token from storage if present
      const token = getaccesstoken();
      if (token) {
        // set headers on axiosInstance
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        axiosInstance.defaults.headers.common['auth-token'] = token;
        setIsAuthenticated(true);
        setUser({ username: 'expert', role: 'expert' });
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async ({ username, password }) => {
    try {
      const resp = await axiosInstance.post(`${apiPrefix}auth/login`, { username, password });

      // only accept explicit success with a token
      if (resp?.status === 200 && resp?.data?.token) {
        const token = resp.data.token;
        // store token and set axios headers
        localStorage.setItem('auth-token', token);
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        axiosInstance.defaults.headers.common['auth-token'] = token;

        const userData = { username, token, role: 'expert' };
        setUser(userData);
        setIsAuthenticated(true);
        setError(null);
        return true;
      }

      // any non-200 or missing token => treat as failure
      const msg = resp?.data?.message || 'Invalid credentials';
      setError(msg);
      setUser(null);
      setIsAuthenticated(false);
      return false;
    } catch (err) {
      // explicit failure path: do NOT silently succeed
      console.error('Login failed:', err?.response?.data || err?.message);
      const serverMsg = err?.response?.data?.message || err?.response?.data || 'Invalid credentials';
      setError(serverMsg);
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const token = getaccesstoken();
      // prefer Authorization header; server accepts either
      await axiosInstance.get(`${apiPrefix}auth/logout`, { token }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
    } catch (error) {
      // ignore server errors on logout, still clear client state
      console.error('Error during logout:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      delete axiosInstance.defaults.headers.common['Authorization'];
      delete axiosInstance.defaults.headers.common['auth-token'];
      localStorage.removeItem('auth-token');
    }
  };

  // Check auth status: prefer server verification if available, otherwise check local token
  const checkAuthStatus = async () => {
    const token = getaccesstoken();
    if (!token) {
      logout();
      return false;
    }

    try {
      // attempt server-side verification endpoint if present
      const resp = await axiosInstance.get(`${apiPrefix}verify-auth`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (resp?.data?.authenticated) {
        setUser(resp.data.user || { username: 'expert', role: 'expert' });
        setIsAuthenticated(true);
        return true;
      } else {
        await logout();
        return false;
      }
    } catch (err) {
      // if verify endpoint isn't available, fall back to trusting local token
      // (still keep token in axiosInstance headers)
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axiosInstance.defaults.headers.common['auth-token'] = token;
      setIsAuthenticated(true);
      setUser({ username: 'expert', role: 'expert' });
      return true;
    }
  };

  // Provide context value
  const contextValue = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    checkAuthStatus,
    error,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const getaccesstoken = () => {
  try {
    const tokenHeader = axiosInstance?.defaults?.headers?.common?.['auth-token'];
    if (tokenHeader) return tokenHeader;
    const authHeader = axiosInstance?.defaults?.headers?.common?.['Authorization'];
    if (authHeader && authHeader.startsWith('Bearer ')) return authHeader.slice(7);
  } catch (err) {
    // ignore
  }

  return (
    localStorage.getItem('auth-token') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('token') ||
    null
  );
};

export default AuthContext;